/**
 * IntegrationsPage.tsx
 *
 * The Integrations Portal — a grid of connector cards that lets a tenant
 * link external accounts via OAuth2 popup flow.
 *
 * Supported connectors:
 *   Microsoft 365 · Google Workspace · Indeed · Salesforce
 *
 * Each card shows:
 *   • Provider logo (SVG icon) + display name
 *   • "Connected" pill with the account email  OR  "Disconnected" badge
 *   • [Connect] button (opens OAuth popup)  OR  [Disconnect] link
 *
 * OAuth popup flow:
 *   1. POST /v1/integrations/auth/initiate  { provider }
 *      → { authUrl }
 *   2. window.open(authUrl) — opens provider consent screen
 *   3. Provider redirects back to /v1/integrations/auth/{provider}/callback
 *   4. Server closes the popup with window.postMessage({ type:'oauth_callback', ... })
 *   5. Page receives the message, fires a toast, refreshes credential status
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { api, apiErrorMessage } from '../api/client'
import { useWriteAccess } from '../lib/writeAccess'

// ── Types ──────────────────────────────────────────────────────────────────────

/** Mirrors contracts.TenantCredential (tokens are never included). */
interface TenantCredential {
  id:           string
  tenantId:     string
  providerName: string
  accountEmail: string
  scopes:       string[]
  expiresAt?:   string
  connectedAt:  string
  updatedAt:    string
}

/** postMessage payload sent by the Go popup-close HTML page. */
interface OAuthCallbackMessage {
  type:     'oauth_callback'
  status:   'success' | 'error'
  provider: string
  email:    string
  error:    string
}

// ── Connector catalogue ────────────────────────────────────────────────────────

type ConnectorDef = {
  id:          string
  name:        string
  description: string
  category:    string
  icon:        React.ReactNode
  docsUrl:     string
}

// Monochrome SVG icons — rendered in the card's brand colour via CSS var.

function IconMicrosoft() {
  return (
    <svg viewBox="0 0 21 21" fill="none" className="w-[22px] h-[22px]" aria-hidden="true">
      <rect x="1" y="1"  width="9" height="9" fill="#f25022"/>
      <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
      <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
    </svg>
  )
}

function IconGoogle() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="w-[22px] h-[22px]" aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  )
}

function IconIndeed() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="w-[22px] h-[22px]" aria-hidden="true">
      <rect width="24" height="24" rx="4" fill="#003A9B"/>
      <path d="M7 7h4v10H7V7zm3-4a2 2 0 1 1-4 0 2 2 0 0 1 4 0z" fill="white"/>
      <path d="M13 10.5c0-2 1.5-3.5 3.5-3.5S20 8.5 20 10.5V17h-4v-6c0-.6-.4-1-1-1s-1 .4-1 1v6h-1V10.5z" fill="white"/>
    </svg>
  )
}

function IconSalesforce() {
  return (
    <svg viewBox="0 0 48 32" fill="none" className="w-[22px] h-[22px]" aria-hidden="true">
      <path d="M20 3.5a8 8 0 0 1 5.5 2.2 9.5 9.5 0 0 1 15 7.8 8 8 0 0 1-1.5 15.7H10A7.5 7.5 0 0 1 10 14a8 8 0 0 1 10-10.5z" fill="#00A1E0"/>
    </svg>
  )
}

const CONNECTORS: ConnectorDef[] = [
  {
    id:          'microsoft365',
    name:        'Microsoft 365',
    description: 'Read email, calendar, and OneDrive files via Microsoft Graph.',
    category:    'Productivity',
    icon:        <IconMicrosoft />,
    docsUrl:     'https://learn.microsoft.com/en-us/graph/overview',
  },
  {
    id:          'google',
    name:        'Google Workspace',
    description: 'Access Gmail, Google Calendar, and Drive for automated workflows.',
    category:    'Productivity',
    icon:        <IconGoogle />,
    docsUrl:     'https://developers.google.com/workspace',
  },
  {
    id:          'indeed',
    name:        'Indeed',
    description: 'Post jobs, pull candidate applications, and track recruitment pipelines.',
    category:    'Recruitment',
    icon:        <IconIndeed />,
    docsUrl:     'https://developer.indeed.com',
  },
  {
    id:          'salesforce',
    name:        'Salesforce',
    description: 'Sync leads, contacts, and opportunities to power AI sales workflows.',
    category:    'CRM',
    icon:        <IconSalesforce />,
    docsUrl:     'https://developer.salesforce.com/docs',
  },
]

// ── Toast ──────────────────────────────────────────────────────────────────────

type ToastKind = 'success' | 'error' | 'info'
interface Toast { id: number; message: string; kind: ToastKind }

function ToastStack({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: number) => void }) {
  return (
    <div className="fixed bottom-7 right-7 z-[100] flex flex-col gap-2 max-w-[340px] pointer-events-none" role="status" aria-live="polite">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={[
            'int-toast-animate flex items-start gap-[9px] px-[14px] py-[11px] rounded-xl text-[12.5px] font-semibold leading-[1.45] pointer-events-auto backdrop-blur-[12px] break-words',
            t.kind === 'success' ? 'bg-[rgba(34,197,94,0.13)] border border-[rgba(34,197,94,0.3)] text-[#4ade80]' :
            t.kind === 'error'   ? 'bg-[rgba(239,68,68,0.13)] border border-[rgba(239,68,68,0.3)] text-[#f87171]' :
                                   'bg-[rgba(56,189,248,0.10)] border border-[rgba(56,189,248,0.25)] text-[#7dd3fc]',
          ].join(' ')}
        >
          <span className="text-[13px] flex-shrink-0 mt-[1px]" aria-hidden="true">
            {t.kind === 'success' ? '✓' : t.kind === 'error' ? '✕' : 'ℹ'}
          </span>
          <span className="flex-1">{t.message}</span>
          <button
            className="ml-auto flex-shrink-0 bg-transparent border-none text-inherit cursor-pointer opacity-50 hover:opacity-100 text-[15px] pl-[6px] leading-none transition-opacity duration-[120ms]"
            onClick={() => onDismiss(t.id)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  )
}

// ── ConnectorCard ──────────────────────────────────────────────────────────────

type ConnectorCardProps = {
  connector:   ConnectorDef
  credential:  TenantCredential | null
  connecting:  boolean
  /** Connect / Reconnect (OAuth initiate) — records access; absent = hidden. */
  onConnect?:   () => void
  /** Disconnect — OWNER only; absent = hidden. */
  onDisconnect?: () => void
}

function ConnectorCard({ connector, credential, connecting, onConnect, onDisconnect }: ConnectorCardProps) {
  const isConnected = credential !== null

  return (
    <article
      className={[
        'flex flex-col gap-3 rounded-2xl p-[22px] pb-[18px] backdrop-blur-[10px] transition-all duration-[180ms] relative overflow-hidden border',
        isConnected
          ? 'bg-[rgba(10,18,28,0.80)] border-[rgba(34,197,94,0.25)] hover:border-[rgba(34,197,94,0.42)] hover:shadow-[0_4px_32px_rgba(34,197,94,0.08)]'
          : 'bg-[rgba(10,18,28,0.72)] border-[rgba(148,163,184,0.1)] hover:border-[rgba(148,163,184,0.22)] hover:shadow-[0_4px_32px_rgba(0,0,0,0.35)] hover:-translate-y-px',
      ].join(' ')}
      aria-label={`${connector.name} integration`}
    >
      {/* Icon */}
      <div
        className="w-[42px] h-[42px] flex items-center justify-center rounded-xl bg-[rgba(6,10,16,0.6)] border border-[rgba(148,163,184,0.1)] flex-shrink-0"
        aria-hidden="true"
      >
        {connector.icon}
      </div>

      {/* Header */}
      <div className="flex items-baseline gap-2">
        <h3 className="text-[15px] font-[800] text-[#c8d9ee] m-0 tracking-[-0.02em]">{connector.name}</h3>
        <span className="text-[9.5px] font-bold tracking-[0.1em] uppercase text-[#3a5068] bg-[rgba(148,163,184,0.07)] border border-[rgba(148,163,184,0.1)] rounded-full px-[7px] py-0.5">
          {connector.category}
        </span>
      </div>

      {/* Description */}
      <p className="text-[12px] text-[#4e6480] leading-[1.6] m-0 flex-1">{connector.description}</p>

      {/* Status */}
      <div className="min-h-6">
        {isConnected ? (
          <div className="flex flex-col gap-1">
            <span className="inline-flex items-center gap-[6px] text-[11px] font-bold px-[9px] py-[3px] pl-[6px] rounded-full w-fit bg-[rgba(34,197,94,0.10)] border border-[rgba(34,197,94,0.25)] text-[#4ade80]">
              <span
                className="connector-status-dot--connected w-[6px] h-[6px] rounded-full flex-shrink-0 bg-[#4ade80]"
                aria-hidden="true"
                style={{ boxShadow: '0 0 0 3px rgba(74,222,128,0.25)' }}
              />
              Connected
            </span>
            {credential.accountEmail && (
              <span
                className="text-[11px] text-[#4e6480] tabular-nums overflow-hidden text-ellipsis whitespace-nowrap max-w-[220px] pl-[21px]"
                title={credential.accountEmail}
              >
                {credential.accountEmail}
              </span>
            )}
          </div>
        ) : (
          <span className="inline-flex items-center gap-[6px] text-[11px] font-bold px-[9px] py-[3px] pl-[6px] rounded-full w-fit bg-[rgba(148,163,184,0.07)] border border-[rgba(148,163,184,0.12)] text-[#4e6480]">
            <span className="w-[6px] h-[6px] rounded-full flex-shrink-0 bg-[#3a5068]" aria-hidden="true" />
            Disconnected
          </span>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 mt-0.5">
        {isConnected ? (
          <>
            {onConnect && <button
              className="inline-flex items-center justify-center gap-[6px] px-[14px] py-2 rounded-[9px] border border-[rgba(148,163,184,0.18)] bg-[rgba(148,163,184,0.08)] text-[#8fa3bd] font-[inherit] text-[12px] font-[800] tracking-[0.02em] cursor-pointer transition-all duration-[130ms] min-h-[34px] whitespace-nowrap hover:not-disabled:bg-[rgba(148,163,184,0.14)] hover:not-disabled:text-[#c8d9ee] disabled:opacity-[0.55] disabled:cursor-not-allowed active:not-disabled:scale-[0.97] flex-none"
              onClick={onConnect}
              disabled={connecting}
              title="Re-authenticate this connection"
            >
              {connecting ? <span className="connector-card__spinner" aria-hidden="true" /> : null}
              {connecting ? 'Connecting…' : 'Reconnect'}
            </button>}
            {onDisconnect && <button
              className="inline-flex items-center justify-center gap-[6px] px-[11px] py-[6px] rounded-[9px] border border-[rgba(239,68,68,0.22)] bg-transparent text-[#f87171] font-[inherit] text-[11px] font-bold cursor-pointer transition-all duration-[130ms] min-h-[34px] whitespace-nowrap hover:bg-[rgba(239,68,68,0.08)] hover:border-[rgba(239,68,68,0.4)]"
              onClick={onDisconnect}
            >
              Disconnect
            </button>}
          </>
        ) : onConnect && (
          <button
            className="flex-1 inline-flex items-center justify-center gap-[6px] px-[14px] py-2 rounded-[9px] border border-[rgba(56,189,248,0.35)] bg-[rgba(56,189,248,0.14)] text-[#38bdf8] font-[inherit] text-[12px] font-[800] tracking-[0.02em] cursor-pointer transition-all duration-[130ms] min-h-[34px] whitespace-nowrap hover:not-disabled:bg-[rgba(56,189,248,0.24)] hover:not-disabled:border-[rgba(56,189,248,0.55)] disabled:opacity-[0.55] disabled:cursor-not-allowed active:not-disabled:scale-[0.97]"
            onClick={onConnect}
            disabled={connecting}
          >
            {connecting ? <span className="connector-card__spinner" aria-hidden="true" /> : null}
            {connecting ? 'Connecting…' : 'Connect'}
          </button>
        )}

        <a
          className="ml-auto text-[10.5px] font-bold text-[#3a5068] no-underline tracking-[0.04em] transition-colors duration-[120ms] whitespace-nowrap hover:text-[#8fa3bd]"
          href={connector.docsUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${connector.name} developer docs`}
        >
          Docs ↗
        </a>
      </div>
    </article>
  )
}

// ── Main page ──────────────────────────────────────────────────────────────────

export function IntegrationsPage() {
  const [credentials, setCredentials] = useState<TenantCredential[]>([])
  const [loadState,   setLoadState]   = useState<'loading' | 'ready' | 'error'>('loading')
  const [connecting,  setConnecting]  = useState<Record<string, boolean>>({})
  const [toasts,      setToasts]      = useState<Toast[]>([])
  // Connect (OAuth initiate) is records-level; Disconnect is OWNER-only
  // (lib/writeAccess; fail closed).
  const { records, owner } = useWriteAccess()
  const toastSeq = useRef(0)

  // ── Toast helpers ──────────────────────────────────────────────────────────

  const addToast = useCallback((message: string, kind: ToastKind = 'info') => {
    const id = ++toastSeq.current
    setToasts((prev) => [...prev, { id, message, kind }])
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4000)
  }, [])

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  // ── Load credentials ───────────────────────────────────────────────────────

  const loadCredentials = useCallback(async () => {
    try {
      const data = await api.get<TenantCredential[]>('/v1/integrations/credentials')
      setCredentials(Array.isArray(data) ? data : [])
      setLoadState('ready')
    } catch {
      setLoadState('error')
    }
  }, [])

  useEffect(() => { void loadCredentials() }, [loadCredentials])

  // ── OAuth popup flow ───────────────────────────────────────────────────────

  // Listen for postMessage from the popup window (sent by the Go callback page).
  useEffect(() => {
    function onMessage(ev: MessageEvent) {
      // Strict origin check — only accept messages from our own origin.
      if (ev.origin !== window.location.origin) return
      const msg = ev.data as Partial<OAuthCallbackMessage>
      if (msg?.type !== 'oauth_callback') return

      if (msg.status === 'success') {
        const connector = CONNECTORS.find((c) => c.id === msg.provider)
        addToast(
          `${connector?.name ?? msg.provider} connected${msg.email ? ` as ${msg.email}` : ''}`,
          'success',
        )
        void loadCredentials()
      } else {
        addToast(msg.error || 'Connection failed', 'error')
      }

      // Clear the connecting spinner for this provider.
      if (msg.provider) {
        setConnecting((prev) => ({ ...prev, [msg.provider!]: false }))
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [addToast, loadCredentials])

  const handleConnect = useCallback(async (connector: ConnectorDef) => {
    setConnecting((prev) => ({ ...prev, [connector.id]: true }))
    try {
      const { authUrl } = await api.post<{ authUrl: string }>(
        '/v1/integrations/auth/initiate',
        { provider: connector.id },
      )
      // Open the OAuth consent screen in a centred popup.
      const w = 520, h = 640
      const left = Math.round(window.screenX + (window.outerWidth - w) / 2)
      const top  = Math.round(window.screenY + (window.outerHeight - h) / 2)
      const popup = window.open(
        authUrl,
        `oauth_${connector.id}`,
        `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`,
      )
      if (!popup) {
        setConnecting((prev) => ({ ...prev, [connector.id]: false }))
        addToast('Popup blocked — please allow popups for this site.', 'error')
        return
      }
      // Detect if the user closes the popup manually without completing OAuth.
      const pollTimer = setInterval(() => {
        if (popup.closed) {
          clearInterval(pollTimer)
          setConnecting((prev) => ({ ...prev, [connector.id]: false }))
        }
      }, 500)
    } catch (err) {
      setConnecting((prev) => ({ ...prev, [connector.id]: false }))
      addToast(
        apiErrorMessage(err, 'Failed to initiate connection'),
        'error',
      )
    }
  }, [addToast])

  const handleDisconnect = useCallback(async (connector: ConnectorDef) => {
    try {
      await api.delete(`/v1/integrations/credentials/${encodeURIComponent(connector.id)}`)
      setCredentials((prev) => prev.filter((c) => c.providerName !== connector.id))
      addToast(`${connector.name} disconnected`, 'info')
    } catch (err) {
      addToast(
        apiErrorMessage(err, 'Failed to disconnect'),
        'error',
      )
    }
  }, [addToast])

  // ── Build credential lookup map ────────────────────────────────────────────

  const credByProvider = Object.fromEntries(
    credentials.map((c) => [c.providerName, c]),
  ) as Record<string, TenantCredential>

  // ── Render ─────────────────────────────────────────────────────────────────

  const connectedCount = credentials.length

  return (
    <div className="px-8 pt-7 pb-12 max-w-[1100px] relative">
      {/* Page header */}
      <div className="flex items-start justify-between gap-6 mb-9">
        <div className="flex-1 min-w-0">
          <h1 className="text-[22px] font-[900] text-[#dce9f7] m-0 mb-2 tracking-[-0.03em]">Integrations</h1>
          <p className="text-[13px] text-[#4e6480] leading-[1.65] m-0 max-w-[520px]">
            {records
              ? "Connect external accounts to let your AI agents read emails, post jobs, update CRM records, and more — all within your tenant's permission scope."
              : "External accounts connected to this workspace let its AI agents read emails, post jobs, update CRM records, and more — all within your tenant's permission scope."}
          </p>
        </div>
        <div className="flex flex-col items-end flex-shrink-0 gap-0.5">
          <span className="text-[32px] font-[900] text-[#38bdf8] leading-none tabular-nums">{connectedCount}</span>
          <span className="text-[11px] font-semibold text-[#4e6480] tracking-[0.06em] uppercase">
            {connectedCount === 1 ? 'connection' : 'connections'} active
          </span>
        </div>
      </div>

      {/* Loading / error states */}
      {loadState === 'loading' && (
        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
          {CONNECTORS.map((c) => (
            <div key={c.id} className="connector-card--skeleton rounded-2xl" aria-hidden="true" />
          ))}
        </div>
      )}

      {loadState === 'error' && (
        <div className="text-[13px] text-[#f87171] flex items-center gap-2 px-[18px] py-[14px] bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.2)] rounded-xl">
          <span aria-hidden="true">⚠</span>
          Failed to load integrations status.{' '}
          <button
            className="bg-transparent border-none text-[#f87171] text-[13px] font-[inherit] cursor-pointer underline p-0 hover:text-[#fca5a5]"
            onClick={() => { setLoadState('loading'); void loadCredentials() }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Connector grid */}
      {loadState === 'ready' && (
        <>
          {/* Category: Productivity */}
          <ConnectorSection
            title="Productivity"
            connectors={CONNECTORS.filter((c) => c.category === 'Productivity')}
            credByProvider={credByProvider}
            connecting={connecting}
            onConnect={records ? handleConnect : undefined}
            onDisconnect={owner ? handleDisconnect : undefined}
          />
          {/* Category: Recruitment */}
          <ConnectorSection
            title="Recruitment"
            connectors={CONNECTORS.filter((c) => c.category === 'Recruitment')}
            credByProvider={credByProvider}
            connecting={connecting}
            onConnect={records ? handleConnect : undefined}
            onDisconnect={owner ? handleDisconnect : undefined}
          />
          {/* Category: CRM */}
          <ConnectorSection
            title="CRM"
            connectors={CONNECTORS.filter((c) => c.category === 'CRM')}
            credByProvider={credByProvider}
            connecting={connecting}
            onConnect={records ? handleConnect : undefined}
            onDisconnect={owner ? handleDisconnect : undefined}
          />
        </>
      )}

      <ToastStack toasts={toasts} onDismiss={dismissToast} />
    </div>
  )
}

// ── ConnectorSection ───────────────────────────────────────────────────────────

type ConnectorSectionProps = {
  title:         string
  connectors:    ConnectorDef[]
  credByProvider: Record<string, TenantCredential>
  connecting:    Record<string, boolean>
  onConnect?:    (c: ConnectorDef) => void
  onDisconnect?: (c: ConnectorDef) => void
}

function ConnectorSection({
  title, connectors, credByProvider, connecting, onConnect, onDisconnect,
}: ConnectorSectionProps) {
  if (connectors.length === 0) return null
  return (
    <section className="mb-10">
      <h2 className="text-[10.5px] font-[800] tracking-[0.14em] uppercase text-[#3a5068] m-0 mb-4">
        {title}
      </h2>
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
        {connectors.map((c) => (
          <ConnectorCard
            key={c.id}
            connector={c}
            credential={credByProvider[c.id] ?? null}
            connecting={connecting[c.id] ?? false}
            onConnect={onConnect ? () => onConnect(c) : undefined}
            onDisconnect={onDisconnect ? () => onDisconnect(c) : undefined}
          />
        ))}
      </div>
    </section>
  )
}
